// postMessage protocol between the iframe and its parent (embed.js loader).
// The loader listens for these messages and resizes the iframe element to
// match the inner content height — keeping the embedded form flush with no
// inner scrollbar regardless of how many custom questions are added.

export const RESIZE_MESSAGE_TYPE = 'coherent:webform:resize'
export const REDIRECT_MESSAGE_TYPE = 'coherent:webform:redirect'

export type WebformResizeMessage = {
    type: typeof RESIZE_MESSAGE_TYPE
    iframeId: string
    height: number
}

export type WebformRedirectMessage = {
    type: typeof REDIRECT_MESSAGE_TYPE
    iframeId: string
    url: string
}

export function sendResizeToParent(iframeId: string, height: number): void {
    if (typeof window === 'undefined' || window.parent === window) return
    const message: WebformResizeMessage = { type: RESIZE_MESSAGE_TYPE, iframeId, height }
    // targetOrigin '*' is acceptable here — the only data leaving is an
    // integer height + an opaque iframe id, no user input. Restricting
    // requires the clinic page's origin to be known, which it isn't from
    // inside the iframe.
    window.parent.postMessage(message, '*')
}

// The iframe cannot navigate the clinic's top-level page from a sandboxed
// context, so the loader script does it on receipt. URL comes from the
// merchant's own webform config (validated as http/https server-side).
export function sendRedirectToParent(iframeId: string, url: string): void {
    if (typeof window === 'undefined' || window.parent === window) return
    const message: WebformRedirectMessage = { type: REDIRECT_MESSAGE_TYPE, iframeId, url }
    window.parent.postMessage(message, '*')
}

export function isResizeMessage(data: unknown): data is WebformResizeMessage {
    if (!data || typeof data !== 'object') return false
    const m = data as { type?: unknown; height?: unknown; iframeId?: unknown }
    return m.type === RESIZE_MESSAGE_TYPE
        && typeof m.height === 'number'
        && isFinite(m.height)
        && typeof m.iframeId === 'string'
}

export function isRedirectMessage(data: unknown): data is WebformRedirectMessage {
    if (!data || typeof data !== 'object') return false
    const m = data as { type?: unknown; url?: unknown; iframeId?: unknown }
    return m.type === REDIRECT_MESSAGE_TYPE
        && typeof m.url === 'string'
        && typeof m.iframeId === 'string'
}
